/**
 * Browser-local softphone recovery lock.
 * Set when this agent accepted a call and the softphone connection failed.
 * Holds CCP offline until an outbound probe succeeds.
 */

export const SOFTPHONE_RECOVERY_LOCK_KEY = 'ccpSoftphoneRecoveryLock'
export const SOFTPHONE_PROBE_COOLDOWN_KEY = 'ccpSoftphoneProbeCooldownUntil'
/** Cooldown after each Test Connection attempt (success or failure). */
export const SOFTPHONE_PROBE_COOLDOWN_MS = 60_000

const LOCK_VALUE = '1'

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

export function isSoftphoneRecoveryLocked(): boolean {
  if (!canUseStorage()) return false
  try {
    return window.localStorage.getItem(SOFTPHONE_RECOVERY_LOCK_KEY) === LOCK_VALUE
  } catch {
    return false
  }
}

export function setSoftphoneRecoveryLocked(locked: boolean): void {
  if (!canUseStorage()) return
  try {
    if (locked) {
      window.localStorage.setItem(SOFTPHONE_RECOVERY_LOCK_KEY, LOCK_VALUE)
    } else {
      window.localStorage.removeItem(SOFTPHONE_RECOVERY_LOCK_KEY)
    }
    window.dispatchEvent(
      new CustomEvent('ccp:softphone-recovery-lock', {detail: {locked}}),
    )
  } catch {
    /* private mode / quota */
  }
}

export function getProbeCooldownUntil(): number {
  if (!canUseStorage()) return 0
  try {
    const raw = window.localStorage.getItem(SOFTPHONE_PROBE_COOLDOWN_KEY)
    const parsed = raw ? Number(raw) : 0
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

export function isProbeOnCooldown(now = Date.now()): boolean {
  return getProbeCooldownUntil() > now
}

export function getProbeCooldownRemainingMs(now = Date.now()): number {
  return Math.max(0, getProbeCooldownUntil() - now)
}

export function startProbeCooldown(now = Date.now()): void {
  if (!canUseStorage()) return
  try {
    window.localStorage.setItem(
      SOFTPHONE_PROBE_COOLDOWN_KEY,
      String(now + SOFTPHONE_PROBE_COOLDOWN_MS),
    )
    window.dispatchEvent(
      new CustomEvent('ccp:softphone-probe-cooldown', {
        detail: {until: now + SOFTPHONE_PROBE_COOLDOWN_MS},
      }),
    )
  } catch {
    /* private mode / quota */
  }
}
